import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { jsx } from "react/jsx-runtime";
const StoreContext = createContext({ store: void 0 });
function StoreProvider(s) {
	return /* @__PURE__ */ jsx(StoreContext.Provider, {
		value: { store: s.store },
		children: s.children
	});
}
function useStore() {
	let s = useContext(StoreContext).store;
	if (!s) throw Error("to share a store you must wrap your component in a StoreProvider component with a store value");
	return s;
}
function useSelector(s) {
	let k = useStore(), A = useRef(k.get(s)), j = () => k.get(s), M = useCallback((O) => k.on(s, () => {
		let j = k.get(s);
		A.current !== j && (A.current = j, O());
	}), []);
	return useSyncExternalStore(M, j);
}
const prepareHeaders = (s) => s, transformBody = (s) => s, transformResponse = (s) => s, paramsSerializer = (s) => {
	let O = "", k = "";
	for (let A of Object.keys(s)) s[A] !== void 0 && (k += O + A + "=" + s[A], O === "" && (O = "&"));
	return k;
}, splitUrl = (s) => {
	let O = {
		url: "",
		params: {}
	}, [k, A] = s.split("?");
	if (O.url = k, A) {
		let s = A.split("&");
		for (let k of s) {
			let s = k.split("="), A = s.shift();
			O.params[A] = s.join("=");
		}
	}
	return O;
};
var BaseError = class extends Error {
	constructor(s, O, k, A, j) {
		super(s);
		let M = splitUrl(O);
		this.url = M.url, this.headers = {};
		try {
			this.body = JSON.parse(k);
		} catch {
			this.body = k;
		}
		if (j) for (let s of j.keys()) this.headers[s] = j.get(s);
		this.params = M.params, this.method = A;
	}
}, ResponseError = class extends BaseError {
	constructor(s, O, k, A, j) {
		let M = O.data, N = M?.message || M?.errorMessage || M?.error;
		super(N || "ResponseError", s, k, j, A), this.response = O, this.name = "ResponseError";
	}
}, TimeoutError = class extends BaseError {
	constructor(s, O, k, A) {
		super(s, s, O, A, k), this.name = "TimeoutError";
	}
}, RequestError = class extends BaseError {
	constructor(s, O, k, A, j) {
		super(k, s, O, j, A), this.name = "RequestError";
	}
}, AbortError = class extends BaseError {
	constructor(s, O, k, A) {
		super(s, s, O, A, k), this.name = "AbortError";
	}
};
function createBaseQuery(s) {
	let O = fillOptions(s), { timeout: k, prepareHeaders: A, paramsSerializer: j, transformBody: M,...N } = O;
	return (s, k = {}) => {
		let j = s.builder(k), M = new AbortController(), P = 0, F = {
			...N,
			...s.fetchOptions,
			signal: M.signal
		};
		return {
			buildedRequest: () => ({
				...j,
				params: {
					...k || {},
					...j.params || {}
				},
				baseUrl: O.baseUrl
			}),
			fetch: async () => {
				j = s.builder(k);
				let N = s.timeout || O.timeout, I = s.method, L = getUrl(O, j), R = A(new Headers(j.headers)), z = I === "GET" ? void 0 : getBody(j.body, R, O), B;
				P = N ? setTimeout(() => {
					M.abort(/* @__PURE__ */ Error("timeout"));
				}, N * 1e3) : 0;
				let V;
				try {
					let M = await fetch(L, {
						headers: R,
						method: I,
						body: z,
						...F
					});
					if (O.requestInterceptor && (M = await O.requestInterceptor(M, () => {
						j = s.builder(k);
						let M = s.method, N = getUrl(O, j), P = A(new Headers(j.headers));
						return fetch(N, {
							headers: P,
							method: M,
							body: z,
							...F
						});
					}, j, k, z)), V = await handleResponse(M, R, O, s, k, z, L), V.error) throw V.error;
				} catch (s) {
					let O = s.stack;
					throw s.message === "abort" || s.message === "The user aborted a request." ? s = new AbortError(L, z, R, I) : s.message === "timeout" || s.message.includes("The operation was aborted.") ? s = new TimeoutError(L, z, R, I) : s instanceof ResponseError || s instanceof TypeError && (s = new RequestError(L, z, s.message, R, I)), B = s, B.stack = O, B;
				} finally {
					clearTimeout(P), B && O.onError && O.onError(B);
				}
				return V.response;
			},
			abort: () => {
				getUrl(O, j), M.abort(/* @__PURE__ */ Error("abort"));
			}
		};
	};
}
var handleResponse = async (s, O, k, A, j, M, N) => {
	try {
		let O = s.headers.get("Content-Type");
		if (O && O.includes("json")) {
			let O = await s.text();
			s.data = O ? JSON.parse(O) : null;
		} else O && O.includes("application/octet-stream") ? s.data = await s.blob() : O && O.includes("multipart/form-data") ? s.data = await s.formData() : s.data = await s.text();
	} catch (s) {
		console.error("Error while parsing the response body"), console.error(s);
	}
	s.succeed = !(s.status < 200 || s.status > 299);
	let P = s;
	k.transformResponse && (P = k.transformResponse(P)), A.transformResponse && (P = A.transformResponse(P, j));
	let F;
	return P.succeed || (F = P.status === 504 ? new TimeoutError(N, M, O, A.method) : new ResponseError(N, P, M, O, A.method)), {
		response: P,
		error: F
	};
}, getBody = (s, O, k) => {
	let A = k.transformBody(s || {}), j = O.get("content-type"), M;
	return M = j?.startsWith("application/json") || j === null && !(A instanceof FormData) ? A ? JSON.stringify(A) : void 0 : A || void 0, M;
}, getUrl = (s, O) => {
	let k = typeof s.baseUrl == "function" ? s.baseUrl() : s.baseUrl, { url: A, params: j } = O, M = k + (k.endsWith("/") ? "" : "/") + A, N = s.paramsSerializer(j || {});
	return N && (M += "?" + N), M;
}, fillOptions = (s) => ({
	...s,
	prepareHeaders: s.prepareHeaders || prepareHeaders,
	transformResponse: s.transformResponse || transformResponse,
	paramsSerializer: s.paramsSerializer || paramsSerializer,
	transformBody: s.transformBody || transformBody,
	timeout: s.timeout || 0
});
function uuidv4() {
	return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (s) => (s ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> s / 4).toString(16));
}
var internalKey = "__api__cache__", ApiStore = class {
	constructor(s, O, k) {
		this.hookRefs = {}, this.subscriptions = [], this.endpoints = [], this.deletionsTimers = {}, this.name = s, this.store = O, this.storeKey = internalKey + s, this.baseQuery = k, O.createAction(this.name + "__INIT", this.storeKey)({ cache: {} });
	}
	getQueryStoreKey(s) {
		return this.storeKey + ".cache." + s;
	}
	getApi() {
		return this.store.get(this.storeKey);
	}
	getHookRefCount(s) {
		return this.hookRefs[s];
	}
	getQueryCache(s) {
		return this.store.get(this.getQueryStoreKey(s));
	}
	getSubscription(s) {
		return this.subscriptions.find((O) => O.subscriptionKey === s);
	}
	getDeletionTimer(s) {
		return this.deletionsTimers[s] || 0;
	}
	createBaseQuery(s, O) {
		return this.baseQuery(s, O);
	}
	getStore() {
		return this.store;
	}
	getEndpoints() {
		return this.endpoints;
	}
	getName() {
		return this.name;
	}
	addEndpoint(s, O) {
		let k = s.name || uuidv4(), { builder: A, method: j, transformResponse: M, timeout: N, providesTags: P, invalidatesTags: F, autoRefetch: I, keepUnusedDataFor: L,...R } = s, z = {
			id: k,
			builder: A,
			method: j,
			transformResponse: M,
			providesTags: P,
			invalidatesTags: F,
			timeout: N,
			fetchOptions: R,
			queryRefetcher: {},
			type: O,
			autoRefetch: I,
			keepUnusedDataFor: L
		};
		return this.getEndpoints(), I && setInterval((s) => {
			Object.values(s.queryRefetcher).forEach((s) => s(!1));
		}, I * 1e3, z), this.endpoints.push(z), z;
	}
	increaseRef(s) {
		let O = this.getHookRefCount(s) || 0;
		this.hookRefs[s] = O + 1;
	}
	decreaseRef(s) {
		let O = this.getHookRefCount(s) || 0;
		this.hookRefs[s] = O - 1;
	}
	addSubscription(s, O, k) {
		this.increaseRef(s), this.subscriptions.push({
			queryKey: s,
			subscriptionKey: O,
			unsubscribe: k
		});
	}
	removeSubscription(s, O) {
		this.decreaseRef(s);
		let k = this.getSubscription(O);
		if (k) {
			k.unsubscribe();
			let s = [...this.subscriptions];
			s.splice(s.indexOf(k), 1), this.subscriptions = s;
		} else throw Error("tried to remove unkown subscription with key: " + O);
	}
	deleteQueryData(s, O) {
		let k = { ...this.getApi().cache }, A = { ...this.hookRefs };
		delete k[O], delete A[O], delete s.queryRefetcher[O], this.hookRefs = A, this.store.createAction("TTQ_DELETE", this.storeKey + ".cache")({ ...k });
	}
	addDeletetionTimer(s, O, k) {
		clearTimeout(this.getDeletionTimer(O)), this.deletionsTimers[O] = setTimeout(() => {
			let k = this.getHookRefCount(O);
			(k === 0 || k === void 0) && this.deleteQueryData(s, O);
		}, k * 1e3);
	}
	updateCache(s, O, k, A) {
		this.store.createAction(s, this.getQueryStoreKey(O))({
			...this.getQueryCache(O),
			...k,
			ref: uuidv4()
		}, A);
	}
	initQueryCache(s, O, k) {
		return this.getQueryCache(s) || this.updateCache("TTQ_INIT", s, {
			queryKey: s,
			error: void 0,
			data: void 0,
			isLoading: k !== !0,
			isFetching: k !== !0,
			isUninitialized: !0,
			endpointId: O.id,
			skip: k
		}, {
			queryKey: s,
			endpointId: O.id,
			skip: k
		}), this.getQueryCache(s);
	}
	invalidateTags(s, O) {
		let k = this.getApi().cache, A = [];
		if (s) {
			for (let O of s) for (let s in k) if (A.indexOf(s) === -1) {
				let j = k[s];
				if (j.skip) {
					A.push(s);
					continue;
				}
				if (j.providesTags !== void 0 && j.providesTags.find((s) => {
					if (typeof s == "string") return O === s;
					if (typeof s == "object") return JSON.stringify(O) === JSON.stringify(s);
				}) !== void 0) {
					let O = this.endpoints.find((s) => s.id === j.endpointId);
					if (O && this.getHookRefCount(s) == 0) {
						this.deleteQueryData(O, s);
						continue;
					}
					let k = O?.queryRefetcher[s];
					k && k(!1), console.log(k), A.push(s);
				}
			}
		}
	}
};
const createFetch = (s, O, k, A) => {
	let j = s.createBaseQuery(O, A);
	return {
		start: async (M) => {
			let N, P, F = { request: {
				calledArgs: A,
				...j.buildedRequest(),
				method: O.method
			} };
			s.updateCache("TTQ_FETCH", k, {
				isFetching: !0,
				isUninitialized: !1,
				isLoading: s.getQueryCache(k)?.isUninitialized,
				skip: !1
			}, F);
			try {
				N = await j.fetch();
			} catch (s) {
				P = s, N = P.response, !(P instanceof AbortError) && !(P instanceof ResponseError) && !(P instanceof TimeoutError) && console.error(P);
			}
			let I = O.type === "query" ? typeof O.providesTags == "function" ? O.providesTags(N, P, A) : O.providesTags : void 0, L = O.type === "mutation" ? typeof O.invalidatesTags == "function" ? O.invalidatesTags(N, P, A) : O.invalidatesTags : void 0, R = {
				data: N?.data,
				isLoading: !1,
				isFetching: !1,
				error: P ? P instanceof AbortError ? void 0 : P : void 0,
				__error: "TEST",
				providesTags: I,
				invalidateTags: L,
				skip: !1
			};
			if (s.updateCache(P ? P instanceof AbortError ? "TTQ_ABORT" : "TTQ_ERROR" : "TTQ_DONE", k, R, {
				...F,
				request: N || P ? {
					calledArgs: A,
					...j.buildedRequest(),
					redirected: N?.redirected,
					status: N?.status,
					statusText: N?.statusText,
					succeed: N?.succeed,
					type: N?.type,
					transformedResponse: N?.data,
					method: O.method,
					error: P ? {
						message: P.message,
						stack: P.stack
					} : void 0
				} : void 0
			}), M === !0 && P) throw P;
			return N;
		},
		abort: j.abort
	};
}, useForceRender = () => {
	let [s, k] = useState();
	return useCallback(() => k({}), []);
};
var MutationPromise = class extends Promise {
	constructor(s, O) {
		super(s), this.unwrap = async () => {
			try {
				return (await this.query)?.data;
			} catch (s) {
				throw s;
			}
		}, this.query = O;
	}
};
const createMutationPromise = (s) => new MutationPromise(((O, k) => {
	s.then((s) => {
		O(s);
	}).catch((s) => {
		if (s instanceof TimeoutError || s instanceof ResponseError || s instanceof RequestError || s instanceof AbortError) return O(s?.response);
		throw s;
	});
}), s), buildMutation = (s, k, j) => {
	let P = () => {
		let j = useForceRender(), P = useMemo(() => k.id + ";" + uuidv4().slice(0, 4), []), F = useRef(() => {});
		return s.initQueryCache(P, k, !0), useEffect(() => {
			let O = s.getStore().on(s.getQueryStoreKey(P) + ".ref", () => j());
			return () => {
				O(), s.addDeletetionTimer(k, P, 0);
			};
		}, []), [
			useCallback((O) => {
				let A = createFetch(s, k, P, O);
				F.current = A.abort;
				let j = A.start(!0).finally(() => {
					let O = s.getQueryCache(P);
					O.error ?? s.invalidateTags(O.invalidateTags, k);
				});
				return createMutationPromise(j);
			}, []),
			{ ...s.getQueryCache(P) },
			F.current
		];
	};
	return P.blank = async (O) => {
		let A = k.id + ";" + uuidv4().slice(0, 4), j = createFetch(s, k, A, O);
		s.initQueryCache(A, k, !1);
		let M = await j.start(!1);
		return s.addDeletetionTimer(k, A, 0), {
			...s.getQueryCache(A),
			...M,
			refetch: j.start,
			abort: j.abort
		};
	}, P;
}, buildQueryKey = (s, O, k) => {
	let A = s.createBaseQuery(O, k).buildedRequest();
	return O.id + ";" + encodeURIComponent(JSON.stringify(A?.params));
}, buildQuery = (s, O, k) => {
	let A = (A, N) => {
		let F = useMemo(() => buildQueryKey(s, O, A), [A]), I = N?.skip || !1, [L, R] = useState(s.initQueryCache(F, O, I)), z = useMemo(() => createFetch(s, O, F, A), [F]);
		useLayoutEffect(() => {
			let A = uuidv4(), j = s.getDeletionTimer(F);
			clearTimeout(j);
			let M = s.getStore().on(s.getQueryStoreKey(F) + ".ref", () => R({ ...s.getQueryCache(F) }));
			s.addSubscription(F, A, M);
			let P = s.getQueryCache(F), L = N?.ignoreCache;
			if (I === !1 && (P?.isUninitialized || L)) try {
				z.start(), O.queryRefetcher[F] = z.start;
			} catch (s) {
				console.error("Error while executing the fetch, this seems to be an internal error of @tecreation/js-state-react"), console.error(s);
			}
			else I == 1 && O.queryRefetcher[F];
			return () => {
				L && s.updateCache("TTQ_IGNORE_CACHE", F, {
					data: N?.defaultData,
					isLoading: !I,
					isFetching: !I,
					isUninitialized: !0
				}), s.removeSubscription(F, A), s.addDeletetionTimer(O, F, O.keepUnusedDataFor || k.keepUnusedDataFor || 0);
			};
		}, [
			I,
			F,
			N?.ignoreCache
		]);
		let B = { ...s.getQueryCache(F) };
		return !B.data && N && N.defaultData && (B = {
			...L,
			data: N.defaultData
		}), {
			...B,
			refetch: z.start,
			abort: z.abort
		};
	};
	return A.blank = async (A) => {
		let j = buildQueryKey(s, O, A), M = createFetch(s, O, j, A);
		s.initQueryCache(j, O, !1);
		let N = s.getQueryCache(j);
		return clearTimeout(s.getDeletionTimer(j)), N.isUninitialized && (await M.start(!1), N = s.getQueryCache(j)), O.queryRefetcher[j] = M.start, s.addDeletetionTimer(O, j, O.keepUnusedDataFor || k.keepUnusedDataFor || 0), {
			...N,
			refetch: M.start,
			abort: M.abort
		};
	}, A;
};
var Api = class {
	constructor(s, O, k, A) {
		this.utils = { refresh: () => {
			let s = [];
			for (let O of this.store.getEndpoints()) Object.values(O.queryRefetcher).forEach((O) => {
				s.push(O(!1));
			});
			return Promise.all(s);
		} }, this.store = new ApiStore(s, O, k), this.apiOptions = {
			keepUnusedDataFor: 0,
			...A
		};
	}
	query(s) {
		s.method = s.method || "GET";
		let O = this.store.addEndpoint(s, "query");
		return buildQuery(this.store, O, this.apiOptions);
	}
	mutation(s) {
		s.method = s.method || "POST";
		let O = this.store.addEndpoint(s, "mutation");
		return buildMutation(this.store, O, this.apiOptions);
	}
}, apis = {};
function createApi(s, O, k, A) {
	return apis[s] || (apis[s] = new Api(s, O, k, A)), apis[s];
}
export { AbortError, RequestError, ResponseError, StoreContext, StoreProvider, TimeoutError, createApi, createBaseQuery, paramsSerializer as defaultParamSerializer, useSelector };
